import { ReactNode, useEffect, useState } from 'react';
import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import { difficultyLevel, ExcursionIcon, MyMscExcursion } from '@msc-b2c/interfaces/interfaces';
import { GetShipsResponse, ItineraryResponse, PdfType } from '@msc-b2c/interfaces/types';
import {
  addDaysAtSea,
  getActBaseImagePath,
  getCurrencySymbol,
  getExursionsActivityIcons,
  getItineraryImgDomain,
  managePlurals,
  replacePlaceholders,
} from '@msc-b2c/shared-utils';

import { styles } from './styles';
import { getItineraryNameByCanvas, getLanguageLabelFromCode } from '../../../utils/myAreaUtils';

type DownloadPDFProps = {
  id?: string;
  excursionImgDMURL?: string;
  excursionImgAltText?: string;
  description?: string;
  buttonLabel?: string;
  buttonIcon?: string;
  headerImgReference?: string;
  keyToSymbolsImgReference?: string;
  cruiseDestinationLabel?: string;
  cruiseShipLabel?: string;
  excursionDurationLabel?: string;
  priceTypeLabel?: string;
  firstPriceLabel?: string;
  secondPriceLabel?: string;
  minimumNoOfParticipantsRequiredText?: string;
  tourLanguageLabel?: string;
  excursionActivityCategories?: ExcursionActivityCategories[];
  dataComponentName: string;
  selectedDay?: string;
  itinerary: ItineraryResponse | null | undefined;
  excursions: MyMscExcursion[];
  geographicalDestination: string;
  shipData?: GetShipsResponse;
  categoryDescription?: string;
  codeLabel?: string;
  itinerariesLabel?: string;
  departureFromLabel?: string;
  cruiseLabel?: string;
  headerImgDMURL?: string;
  footerImgDMURL?: string;
  keyToSymbolsImgDMURL?: string;
  pdfType?: PdfType;
  ourExcursionsLabel?: string;
  keyToSymbolsLabel?: string;
  dayFieldLabel?: string;
  nightFieldLabel?: string;
  departureDateLabel?: string;
  copyrightFooter?: string;
  generatedLabelDateFooter?: string;
};

type ExcursionActivityCategories = {
  categoryColor: string;
  categoryCode: string;
  categoryTitle: string;
  categoryDescription?: string;
};

type CategoryDirectory = {
  [key: string]: {
    categoryTitle: string;
    categoryColor: string;
    order: number;
  };
};

type ExcursionsIconsDirectory = {
  [key: string]: string;
};

type NormalizedExcursion = MyMscExcursion & {
  activityGroup: NonNullable<MyMscExcursion['activityGroup']> & {
    details: string;
    description: string;
  };
  activityDetails: NonNullable<MyMscExcursion['activityDetails']>;
};

type RichTextSegment = {
  text: string;
  bold?: boolean;
};

type RichTextParagraph = RichTextSegment[];

function trimDescriptionParagraphsBeforePrice(
  richDescriptionParagraphs: RichTextParagraph[]
): RichTextParagraph[] {
  const displayDescriptionParagraphs: RichTextParagraph[] = [];
  for (const paragraph of richDescriptionParagraphs) {
    const paragraphText = paragraph.map((segment) => segment.text).join('');
    const priceLabelMatch = /price\s*per\s*person/i.exec(paragraphText);
    if (!priceLabelMatch) {
      displayDescriptionParagraphs.push(paragraph);
      continue;
    }

    const textBeforePrice = paragraphText.slice(0, priceLabelMatch.index).trim();
    if (textBeforePrice) {
      displayDescriptionParagraphs.push([{ text: textBeforePrice }]);
    }
    break;
  }
  return displayDescriptionParagraphs;
}

function stripTrailingPriceSection(rawDescription: string): string {
  return rawDescription.replace(/price[\s\u00a0]*per[\s\u00a0]*person[\s\S]*$/i, '').trim();
}

function formatDuration(duration: number | string | undefined) {
  if (duration === undefined || duration === null || duration === '') return '';
  const numericDuration = Number(duration);
  if (Number.isNaN(numericDuration)) return `${duration}`;
  const hours = Math.floor(numericDuration);
  const minutes = Math.round((numericDuration - hours) * 60);
  if (minutes === 0) return `${hours}`;
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

function formatCodeLabel(codeText: string, configuredLabel: string) {
  const trimmedLabel = configuredLabel.trim();
  if (!trimmedLabel) return codeText;
  const normalizedLabel = trimmedLabel.endsWith(':') ? trimmedLabel : `${trimmedLabel}:`;
  return `${normalizedLabel} ${codeText}`;
}

function normalizeDate(value?: string) {
  return value ? value.slice(0, 10) : '';
}

function sanitizeHtmlToText(html: string) {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p(?:\s+class=["']?.*?["']?)?>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
}

function toAbsoluteAssetUrl(path: string) {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:image/')) {
    return path;
  }

  const origin = globalThis.location?.origin || '';
  if (path.startsWith('/')) {
    return origin ? `${origin}${path}` : path;
  }

  return origin ? `${origin}/${path}` : path;
}

export function PDFDocument({
  headerImgReference = '',
  keyToSymbolsImgReference = '',
  cruiseDestinationLabel = '',
  cruiseShipLabel = '',
  excursionDurationLabel = '',
  priceTypeLabel = '',
  firstPriceLabel = '',
  secondPriceLabel = '',
  minimumNoOfParticipantsRequiredText = '',
  tourLanguageLabel = '',
  excursionActivityCategories = [],
  itinerary,
  excursions,
  selectedDay,
  geographicalDestination,
  shipData,
  categoryDescription = '',
  codeLabel = '',
  itinerariesLabel = '',
  departureFromLabel = '',
  cruiseLabel = '',
  pdfType = PdfType.DayPort,
  ourExcursionsLabel,
  keyToSymbolsLabel,
  dayFieldLabel,
  nightFieldLabel,
  departureDateLabel,
  copyrightFooter = '',
  generatedLabelDateFooter = '',
}: Readonly<DownloadPDFProps>): ReactNode {
  const [itiImage, setItiImage] = useState<string>('');

  const date = new Date();
  const formatDate = `${date.getFullYear()}-${
    date.getMonth() + 1 > 10 ? date.getMonth() + 1 : '0' + (date.getMonth() + 1)
  }-${date.getDate() > 10 ? date.getDate() : '0' + date.getDate()} ${
    date.getHours() > 10 ? date.getHours() : '0' + date.getHours()
  }:${date.getMinutes() > 10 ? date.getMinutes() : '0' + date.getMinutes()}`;

  const urlCountry = getItineraryImgDomain();
  const activityImageDomain = getActBaseImagePath() || '/content/dam/msc-cruises/b2c-assets/act';
  const mapImagePath = itinerary?.mapImage || '';
  const mapSeparator = mapImagePath.startsWith('/') ? '' : '/';
  // Same retrieval logic used by search app: itineraryImgDomain + mapImage (Mulesoft path)
  const imageSrcDesktop = mapImagePath ? `${urlCountry}${mapSeparator}${mapImagePath}` : '';

  const decodeHtmlEntities = (str: string) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  };

  const fetchImageAsBase64 = async (url: string): Promise<string> => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Map image request failed: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();

    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string) || ''); // this gives you the full data URI
      reader.readAsDataURL(blob);
    });
  };

  const parseParagraphsFromHtml = (htmlString: string) => {
    const decoded = decodeHtmlEntities(htmlString);
    const regex = /(.*?)<p(?:\s+class=["']?.*?["']?)?>(.*?)<\/p>/g;
    const result = [];
    let lastIndex = 0;

    for (const match of decoded.matchAll(regex)) {
      const [full, before = '', inside = ''] = match;
      const start = match.index;

      if (before.trim()) result.push(sanitizeHtmlToText(before));
      if (inside.trim()) result.push(sanitizeHtmlToText(inside));

      lastIndex = start + full.length;
    }

    const after = decoded.slice(lastIndex).trim();
    if (after) result.push(sanitizeHtmlToText(after));

    return result.filter(Boolean);
  };

  const parseRichParagraphsFromHtml = (htmlString: string): RichTextParagraph[] => {
    const decoded = decodeHtmlEntities(htmlString);
    const boldStart = '__BOLD_START__';
    const boldEnd = '__BOLD_END__';
    const normalized = decoded
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p(?:\s+class=["']?.*?["']?)?>/gi, '\n\n')
      .replace(/<p(?:\s+class=["']?.*?["']?)?>/gi, '')
      .replace(/<\/p>/gi, '')
      .replace(/<(strong|b)>/gi, boldStart)
      .replace(/<\/(strong|b)>/gi, boldEnd)
      .replace(/<[^>]+>/g, '')
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/[ \t]+\n/g, '\n')
      .trim();

    const toRichSegments = (value: string): RichTextSegment[] => {
      const result: RichTextSegment[] = [];
      const boldRegex = /__BOLD_START__([\s\S]*?)__BOLD_END__/g;
      let cursor = 0;
      let match = boldRegex.exec(value);
      while (match) {
        const [fullMatch, boldText = ''] = match;
        const startIndex = match.index;
        if (startIndex > cursor) {
          const normalText = value.slice(cursor, startIndex);
          if (normalText) result.push({ text: normalText });
        }
        if (boldText) result.push({ text: boldText, bold: true });
        cursor = startIndex + fullMatch.length;
        match = boldRegex.exec(value);
      }
      if (cursor < value.length) {
        const remainingText = value.slice(cursor);
        if (remainingText) result.push({ text: remainingText });
      }
      return result;
    };

    return normalized
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
      .map((paragraph) => toRichSegments(paragraph))
      .filter((paragraph) => paragraph.length > 0);
  };

  const IS_ALL_EXCURSIONS = selectedDay === 'allPorts' || pdfType === PdfType.FullCruiseItinerary;

  const reverseIconsTable = new Set(['isGuideAvailable', 'isFilmingAllowed']);
  const excursionIcons: ExcursionIcon[] | undefined = getExursionsActivityIcons();
  const excursionIconsDirectory: ExcursionsIconsDirectory = {};
  excursionIcons?.map(
    (excIcon) => (excursionIconsDirectory[excIcon.activityCode] = excIcon.activityIcon)
  );

  function formatExcursionDate(date: string) {
    const dateObj = new Date(date);
    const formatted = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(dateObj);

    return formatted.replace(',', '');
  }

  const categoryDirectory: CategoryDirectory = {};

  excursionActivityCategories.forEach((category, index) => {
    if (category.categoryCode)
      categoryDirectory[category.categoryCode] = {
        categoryTitle: category.categoryTitle,
        categoryColor: category.categoryColor,
        order: index,
      };
  });

  const sortedExcursions: NormalizedExcursion[] = [...excursions]
    .filter(
      (exc): exc is NormalizedExcursion =>
        !!exc.activityGroup?.details && !!exc.activityGroup?.description && !!exc.activityDetails
    )
    .sort((a, b) => {
      const aOrder = categoryDirectory[a.activityGroup.details]?.order ?? Infinity;
      const bOrder = categoryDirectory[b.activityGroup.details]?.order ?? Infinity;

      return aOrder - bOrder;
    });

  useEffect(() => {
    let isMounted = true;
    if (!imageSrcDesktop) {
      setItiImage('');
      return () => {
        isMounted = false;
      };
    }

    fetchImageAsBase64(imageSrcDesktop)
      .then((res) => {
        if (isMounted && res) {
          setItiImage(res);
        }
      })
      .catch((error) => {
        console.warn('[DownloadPDF][Map] base64 conversion failed, fallback to direct URL:', error);
        if (isMounted) {
          setItiImage(imageSrcDesktop);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [imageSrcDesktop, mapImagePath, urlCountry]);

  const dateStr = itinerary?.days?.[0].date ?? '';
  const [year, month, day] = dateStr.split('-');
  const formatted = `${day}/${month}/${year}`;
  const hasMapImage = !!imageSrcDesktop;
  const resolvedItineraryImage = itiImage || imageSrcDesktop;
  const selectedDayData = itinerary?.days?.find(
    (dayItem) => normalizeDate(dayItem.date) === normalizeDate(selectedDay)
  );
  const byDayPortTitle = selectedDayData?.port?.name ?? '';
  const itineraryDaysForExcursions = itinerary ? addDaysAtSea(itinerary, '').days : [];

  const languageArray: string[] = [];
  excursions.forEach((exc) => {
    exc.activityDetails?.guideLanguageCodes?.forEach((lang) => {
      if (!languageArray.includes(lang)) languageArray.push(lang);
    });
  });

  const FOOTER_TEXT_LANGUAGE = replacePlaceholders(categoryDescription, {
    languages: languageArray.join(','),
  });

  const excursion = (exc: NormalizedExcursion) => {
    const category = categoryDirectory[exc.activityGroup.details];
    const detailsObject = exc.activityDetails as unknown as Record<string, unknown>;
    const excursionTitle = decodeHtmlEntities(exc.description || '');
    const excursionDescriptionRaw = exc.longDescription || exc.description || '';
    const excursionDescriptionSanitized = stripTrailingPriceSection(excursionDescriptionRaw);
    const normalizedActivityImageDomain = activityImageDomain.endsWith('/')
      ? activityImageDomain.slice(0, -1)
      : activityImageDomain;
    // Align with excursion cards logic to keep image source consistent across UI and PDF.
    const excursionImage = toAbsoluteAssetUrl(`${normalizedActivityImageDomain}/${exc.code}.jpg`);
    const hasRenderableImage =
      !!excursionImage &&
      (excursionImage.startsWith('http://') ||
        excursionImage.startsWith('https://') ||
        excursionImage.startsWith('data:image/'));
    const shouldRenderImage = hasRenderableImage && !IS_ALL_EXCURSIONS;
    const guideLanguageCodes = exc.activityDetails?.guideLanguageCodes ?? [];
    const durationIconSrc = excursionIconsDirectory['clock'];
    const hasGuideLanguages = guideLanguageCodes.length > 0;
    let languageLabelText = '-';
    if (guideLanguageCodes.length === 1) {
      languageLabelText = getLanguageLabelFromCode(guideLanguageCodes[0]);
    } else if (guideLanguageCodes.length > 1) {
      languageLabelText = guideLanguageCodes
        .map((lang) => getLanguageLabelFromCode(lang))
        .join(', ');
    }
    const richDescriptionParagraphs = parseRichParagraphsFromHtml(excursionDescriptionSanitized);
    const displayDescriptionParagraphs =
      trimDescriptionParagraphsBeforePrice(richDescriptionParagraphs);

    return (
      <View
        style={IS_ALL_EXCURSIONS ? styles.fullItineraryCardContainer : styles.cardContainer}
        wrap={!IS_ALL_EXCURSIONS}
      >
        {!IS_ALL_EXCURSIONS && (
          <View
            style={[
              styles.cardTitleContainer,
              { backgroundColor: category?.categoryColor || '#f2f2f5' },
            ]}
          >
            <Text style={styles.cardTitle}>
              {category?.categoryTitle || exc.activityGroup.description}
            </Text>
          </View>
        )}
        <View
          style={shouldRenderImage ? styles.cardHeaderContainer : styles.cardHeaderContainerNoImage}
        >
          {shouldRenderImage && (
            <Image
              style={styles.cardHeaderImage}
              src={excursionImage}
            />
          )}
          <View
            style={
              shouldRenderImage
                ? styles.cardHeaderInformationContainer
                : styles.cardHeaderInformationContainerNoImage
            }
          >
            <View style={styles.cardHeaderInformationTitle}>
              <Text style={styles.cardHeaderInformationTitleText}>{excursionTitle}</Text>
            </View>
            <Text style={styles.cardHeaderInformationCode}>
              ({formatCodeLabel(exc.code, codeLabel)})
            </Text>
            <View
              style={
                shouldRenderImage
                  ? styles.cardHeaderIconsContainer
                  : styles.cardHeaderIconsContainerNoImage
              }
            >
              {durationIconSrc && (
                <Image
                  src={durationIconSrc}
                  style={styles.cardHeaderIconsContainerDurationIcon}
                />
              )}
              <View style={styles.cardHeaderIconsDurationContainer}>
                <Text style={styles.cardHeaderIconsDurationLabel}>{excursionDurationLabel}</Text>
                <Text style={styles.cardHeaderIconsDurationValue}>
                  {formatDuration(exc.duration)} h
                </Text>
              </View>
              <View style={styles.cardIconsContainer}>
                {Object.entries(detailsObject)
                  .map(([key, value]) => {
                    const updatedValue = reverseIconsTable.has(key) ? !value : value;
                    return [key, updatedValue] as [string, unknown];
                  })
                  .filter(
                    ([key, value]) =>
                      typeof value === 'boolean' && value && excursionIconsDirectory[key]
                  )
                  .map(([key]: [string, unknown]) => (
                    <Image
                      key={key}
                      src={excursionIconsDirectory[key]}
                      style={styles.IconImage}
                    />
                  ))}

                {exc.activityDetails.difficultyLevel &&
                  excursionIconsDirectory[
                    exc.activityDetails.difficultyLevel.toLocaleLowerCase()
                  ] &&
                  Object.values(difficultyLevel).includes(
                    exc.activityDetails.difficultyLevel as difficultyLevel
                  ) && (
                    <Image
                      src={
                        excursionIconsDirectory[
                          exc.activityDetails.difficultyLevel.toLocaleLowerCase()
                        ]
                      }
                      style={styles.IconImage}
                    />
                  )}
                {guideLanguageCodes.length > 0 && (
                  <Image
                    src={
                      excursionIconsDirectory[
                        guideLanguageCodes.length == 1 && guideLanguageCodes.includes('ENG')
                          ? 'engOnly'
                          : 'multiLang'
                      ]
                    }
                    style={styles.IconImage}
                  />
                )}
              </View>
            </View>
          </View>
        </View>
        <View style={styles.cardBodyContainer}>
          <View style={styles.cardBodyLongDescriptionContainer}>
            {displayDescriptionParagraphs.map((paragraph) => (
              <View
                key={`paragraph-${paragraph.map((segment) => segment.text).join('')}`}
                style={styles.cardBodyParagraph}
              >
                <Text style={styles.cardBodyLongDescription}>
                  {paragraph.map((segment) => segment.text).join('')}
                </Text>
              </View>
            ))}
          </View>
          <View
            style={styles.cardBodyFooter}
            wrap={false}
            minPresenceAhead={170}
          >
            <View style={styles.cardBodyPerPersonContainer}>
              <Text style={styles.cardBodyPerPersonLabel}>{priceTypeLabel}</Text>
            </View>
            <View style={styles.cardBodyPricesContainer}>
              <Text style={styles.cardBodyPriceLine}>
                {'\u2022 ' + firstPriceLabel}
                {getCurrencySymbol()}
                {exc.prices.perAdult?.total.afterDiscount}
              </Text>
              <Text style={styles.cardBodyPriceLine}>
                {'\u2022 ' + secondPriceLabel}
                {getCurrencySymbol()}
                {exc.prices.perChild?.total.afterDiscount}
              </Text>
            </View>
            <Text style={styles.cardBodyMinimumParticipants}>
              {minimumNoOfParticipantsRequiredText} {exc.activityDetails.minimumPassengersRequired}
            </Text>
            {hasGuideLanguages && (
              <View style={styles.cardBodyLanguageContainer}>
                <Text style={styles.cardBodyLanguageLabel}>{tourLanguageLabel}</Text>
                <Text style={styles.cardBodyLanguageValues}>{languageLabelText}</Text>
              </View>
            )}
          </View>
        </View>
        {!IS_ALL_EXCURSIONS && <View style={styles.cardBottomDivider} />}
      </View>
    );
  };

  const isByDayPdf = !IS_ALL_EXCURSIONS;

  return (
    <Document>
      <Page
        style={styles.page}
        size="A4"
      >
        <View
          style={styles.header}
          fixed
        >
          <Image src={headerImgReference} />
        </View>
        <View style={styles.pageContent}>
          {isByDayPdf ? (
            <>
              <View style={styles.metaRowsContainer}>
                <View style={styles.metaRow}>
                  <Text>{cruiseDestinationLabel}</Text>
                  <Text style={styles.metaRowValue}>
                    {getItineraryNameByCanvas(geographicalDestination)}
                  </Text>
                </View>
                <View style={styles.metaRow}>
                  <Text>{cruiseShipLabel}</Text>
                  <Text style={styles.metaRowValue}>{shipData?.shipName}</Text>
                </View>
              </View>
              {!!byDayPortTitle && (
                <View style={styles.byDayTitleContainer}>
                  <Text style={styles.byDayTitle}>{byDayPortTitle}</Text>
                </View>
              )}
              <View>{sortedExcursions?.map(excursion)}</View>
            </>
          ) : (
            <View style={styles.allItineraryFullContainer}>
              <View>
                <View
                  style={
                    hasMapImage ? styles.allItineraryContainer : styles.allItineraryContainerNoMap
                  }
                >
                  {hasMapImage && resolvedItineraryImage && (
                    <Image
                      src={resolvedItineraryImage}
                      style={styles.itineraryImage}
                    />
                  )}
                  <View style={hasMapImage ? styles.tableContainer : styles.tableContainerNoMap}>
                    <View style={styles.tableRow}>
                      <Text style={styles.tableLabel}>{cruiseLabel}</Text>
                      <Text style={styles.tableValue}>
                        {getItineraryNameByCanvas(geographicalDestination) + ' '}
                      </Text>
                    </View>
                    <View style={styles.tableRow}>
                      <Text style={styles.tableLabel}> </Text>
                      <Text style={styles.tableValue}>{itinerary?.description}</Text>
                    </View>
                    <View style={styles.tableRow}>
                      <Text style={styles.tableLabel}>{cruiseShipLabel}</Text>
                      <Text style={styles.tableValue}>{shipData?.shipName}</Text>
                    </View>
                    <View style={styles.tableRow}>
                      <Text style={styles.tableLabel}>{departureFromLabel}</Text>
                      <Text style={styles.tableValue}>{itinerary?.days?.[0].port.name}</Text>
                    </View>
                    <View style={styles.tableRow}>
                      <Text style={styles.tableLabel}>{itinerariesLabel}</Text>
                      <Text style={styles.tableValue}>
                        {itinerary?.days
                          .slice(1)
                          .map((day) => day.port.name)
                          .join(', ')}
                      </Text>
                    </View>
                    <View style={styles.tableRow}>
                      <Text style={styles.tableLabel}>{departureDateLabel}</Text>
                      <Text style={styles.tableValue}>{formatted}</Text>
                    </View>
                    <View style={styles.tableRow}>
                      <Text style={styles.tableLabel}>{excursionDurationLabel}</Text>
                      <Text style={styles.tableValue}>
                        {itinerary?.numberOfSailingDays}{' '}
                        {managePlurals(Number(itinerary?.numberOfSailingDays), dayFieldLabel)},{' '}
                        {Number(itinerary?.numberOfSailingDays) - 1}
                        {managePlurals(Number(itinerary?.numberOfSailingDays) - 1, nightFieldLabel)}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
              <View>
                {itineraryDaysForExcursions.map((day) => {
                  const filteredExursionsByDay = sortedExcursions.filter(
                    (exc) => normalizeDate(exc.startDate) === normalizeDate(day.date)
                  );
                  return (
                    filteredExursionsByDay.length > 0 && (
                      <View key={day.date}>
                        <View style={styles.allItineraryDayContainer}>
                          <Text style={styles.allItineraryDayContainerTitle}> {day.port.name}</Text>
                          <Text style={styles.allItineraryDayContainerDate}>
                            {' '}
                            {formatExcursionDate(day.date)}
                          </Text>
                        </View>

                        {filteredExursionsByDay.map((exc, index, arr) => {
                          const isNewGroup =
                            index === 0 ||
                            exc.activityGroup.description !==
                              arr[index - 1].activityGroup.description;

                          return (
                            <View key={`${day.date}-${exc.code}-${index}`}>
                              {isNewGroup && categoryDirectory[exc.activityGroup.details] && (
                                <View
                                  style={[
                                    styles.fullItineraryCategoryTitleContainer,
                                    {
                                      backgroundColor:
                                        categoryDirectory[exc.activityGroup.details].categoryColor,
                                    },
                                  ]}
                                >
                                  <Text style={styles.cardTitle}>
                                    {categoryDirectory[exc.activityGroup.details].categoryTitle}
                                  </Text>
                                </View>
                              )}
                              {excursion(exc)}
                            </View>
                          );
                        })}
                      </View>
                    )
                  );
                })}
              </View>
            </View>
          )}
          <View
            style={[styles.center, styles.keySymbolsContainer]}
            wrap={false}
          >
            <Text style={styles.textBold}>{keyToSymbolsLabel}</Text>
            <Image
              style={styles.keyImage}
              src={keyToSymbolsImgReference}
            />
          </View>
          <View wrap={false}>
            <View style={styles.center}>
              <Text style={styles.textBold}>{ourExcursionsLabel}</Text>
            </View>
            <View style={styles.categorySection}>
              {excursionActivityCategories.map((activity) => (
                <View
                  key={activity?.categoryCode}
                  style={[
                    styles.categorySectionCard,
                    { backgroundColor: activity.categoryColor || 'white' },
                  ]}
                >
                  <Text style={styles.categorySectionCardCategoryTitle}>
                    {activity.categoryTitle}
                  </Text>
                  <View style={styles.categorySectionCardCategoryDescription}>
                    <Text style={styles.categorySectionCardCategoryDescriptionText}>
                      {activity.categoryDescription}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
          <View style={styles.footerDescriptionContainer}>
            {parseParagraphsFromHtml(FOOTER_TEXT_LANGUAGE).map((content) => (
              <Text
                key={content}
                style={styles.cardBodyLongDescription}
              >
                {content}
              </Text>
            ))}
          </View>
        </View>
        <View
          style={styles.footer}
          fixed
        >
          <View style={styles.footerCopyrightText}>
            <Text>
              {parseParagraphsFromHtml(
                replacePlaceholders(copyrightFooter, {
                  year: date.getFullYear().toString(),
                })
              )}
            </Text>
            <Text style={{ margin: '0 auto' }}>{globalThis.location.hostname}</Text>
          </View>
          <Text style={styles.timeDocWasGeneratedText}>
            {parseParagraphsFromHtml(
              replacePlaceholders(generatedLabelDateFooter, {
                fullDate: formatDate,
              })
            )}
          </Text>
        </View>
      </Page>
    </Document>
  );
}
